import { execAdmin } from '@src/apis/reware/utils';
import { requirePartner } from '@src/providers/dataProvider/shared';
import { serializeConditionItem } from '@src/components/catalog/CatalogRules/types';
import type { SerializedCondition } from '@src/components/catalog/CatalogRules/types';

import { getDefinedConditions } from './types';
import type { WizardState } from './types';

/** Raw values keyed by DB column path (the `x-column`/`x-columns` entries from `catalogTemplateDefinitions`). */
export interface CatalogTemplateItemRow {
  [column: string]: string | number | null;
}

export interface CatalogTemplateFetchResult {
  rows: CatalogTemplateItemRow[];
  count: number;
  totalCount: number;
}

interface CatalogItemsForTemplateResponse {
  catalogItemsForTemplate: {
    count: number;
    totalCount: number;
    rows: CatalogTemplateItemRow[] | null;
  } | null;
}

interface CatalogItemsForTemplateVariables {
  partnerId: number;
  conditions: SerializedCondition[] | null;
  fields: string[];
  page: number;
  pageSize: number;
}

const QUERY = `
  query catalogItemsForTemplate(
    $partnerId: Int!
    $conditions: [CatalogRuleConditionInputAdmin!]
    $fields: [String!]!
    $page: Int
    $pageSize: Int
  ) {
    catalogItemsForTemplate(
      partnerId: $partnerId
      conditions: $conditions
      fields: $fields
      page: $page
      pageSize: $pageSize
    ) {
      count
      totalCount
      rows
    }
  }
`;

const PAGE_SIZE = 500;

/**
 * Serializes scope conditions for the backend, dropping any still-blank rule and falling back to
 * `null` if nothing is defined yet — the backend rejects a condition that has neither a `field`
 * nor a non-empty `conditions` array.
 */
const toConditionInputs = (state: WizardState): SerializedCondition[] | null => {
  if (state.scopeMode !== 'subset') return null;
  const definedConditions = getDefinedConditions(state);
  return definedConditions.length > 0 ? definedConditions.map(serializeConditionItem) : null;
};

export interface CatalogTemplateMatchCount {
  count: number;
  totalCount: number;
}

/**
 * Lightweight match-count check for the Scope step's live "N of M records match" preview.
 * Requests a single row purely to read `count`/`totalCount` off the response — unlike
 * `fetchCatalogItemsForTemplate`, it never loops through every page of matching rows, since the
 * preview only ever displays the counts, not the row data itself.
 */
export async function fetchCatalogItemMatchCount(state: WizardState): Promise<CatalogTemplateMatchCount> {
  const partner = requirePartner();
  const conditions = toConditionInputs(state);

  const vars: CatalogItemsForTemplateVariables = {
    partnerId: partner.id,
    conditions,
    fields: [],
    page: 1,
    pageSize: 1,
  };
  const data = await execAdmin<CatalogItemsForTemplateVariables, CatalogItemsForTemplateResponse>(
    QUERY,
    { variables: vars },
  );
  const result = data.catalogItemsForTemplate;
  return { count: result?.count ?? 0, totalCount: result?.totalCount ?? 0 };
}

/**
 * Fetches catalog items for template generation. Always requests every column the partner's
 * template catalog could need (`fields`) regardless of the user's later Identifier/Field
 * selections — those later steps only choose which already-fetched columns are included in
 * the CSV, mirroring the Resale Pricing override-template wizard's approach of fetching once
 * after Scope.
 */
export async function fetchCatalogItemsForTemplate(
  state: WizardState,
  fields: string[],
): Promise<CatalogTemplateFetchResult> {
  const partner = requirePartner();
  const conditions = toConditionInputs(state);

  const firstVars: CatalogItemsForTemplateVariables = {
    partnerId: partner.id,
    conditions,
    fields,
    page: 1,
    pageSize: PAGE_SIZE,
  };
  const firstData = await execAdmin<CatalogItemsForTemplateVariables, CatalogItemsForTemplateResponse>(
    QUERY,
    { variables: firstVars },
  );

  const result = firstData.catalogItemsForTemplate;
  if (!result?.rows) {
    return { rows: [], count: result?.count ?? 0, totalCount: result?.totalCount ?? 0 };
  }

  const allRows: CatalogTemplateItemRow[] = [...result.rows];
  const totalPages = Math.ceil((result.count ?? 0) / PAGE_SIZE);
  for (let page = 2; page <= totalPages; page += 1) {
    const data = await execAdmin<CatalogItemsForTemplateVariables, CatalogItemsForTemplateResponse>(
      QUERY,
      { variables: { ...firstVars, page } },
    );
    if (data.catalogItemsForTemplate?.rows) {
      allRows.push(...data.catalogItemsForTemplate.rows);
    }
  }

  return { rows: allRows, count: result.count ?? 0, totalCount: result.totalCount ?? 0 };
}
